import {
  Activity,
  Bell,
  CheckCircle2,
  Clock3,
  Eye,
  RefreshCw,
  Send,
  ShieldCheck,
  Store,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminState } from "../components/admin/AdminState";
import { StatCard } from "../components/admin/StatCard";
import { StatusBadge } from "../components/admin/StatusBadge";
import { adminPost, type AdminMetric, type AdminReviewRequest, useAdminQuery } from "../components/admin/adminApi";

type OverviewResponse = {
  actionQueue: Array<{ label: string; text: string; tone: "critical" | "info" | "success" | "warning" }>;
  funnel: Array<{ label: string; value: number }>;
  metrics: AdminMetric[];
  ok: true;
  queueSummary: {
    activeRequestCount: number;
    items: Array<{ dueAt: string; id: string; order: string; status: string }>;
    nextSendAt: string | null;
    queuedCount: number;
  };
  requests: AdminReviewRequest[];
  statusMix: Array<{ label: string; value: number }>;
  store: { name: string; shopDomain: string };
};

export function AdminHome() {
  const overview = useAdminQuery<OverviewResponse>("/api/admin/overview");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "critical" | "success"; text: string }>();

  async function sendTestRequest() {
    setBusy(true);
    setNotice(undefined);
    try {
      await adminPost("/api/admin/review-requests/test", {});
      setNotice({ tone: "success", text: "Test request created from the first synced product." });
      await overview.reload();
    } catch (error) {
      setNotice({ tone: "critical", text: error instanceof Error ? error.message : "Could not create a test request." });
    } finally {
      setBusy(false);
    }
  }

  const data = overview.data;
  const maxFunnelValue = Math.max(1, ...(data?.funnel.map((row) => row.value) ?? [1]));
  const totalStatus = data?.statusMix.reduce((total, row) => total + row.value, 0) ?? 0;
  const urgentItems = data?.actionQueue.filter((item) => item.tone === "critical" || item.tone === "warning").length ?? 0;

  return (
    <div className="wr-page wr-overview-page">
      <header className="wr-overview-hero">
        <div className="wr-overview-hero__copy">
          <span>Review operations dashboard</span>
          <h1>Control room for customer proof</h1>
          <p>
            {data?.store
              ? `${data.store.name} on ${data.store.shopDomain}: review requests, moderation, widgets, and message health.`
              : "Review requests, moderation, widgets, and message health."}
          </p>
        </div>
        <div className="wr-overview-hero__actions">
          <button className="wr-button wr-button--secondary" type="button" onClick={() => void overview.reload()}>
            <RefreshCw size={15} aria-hidden />
            Refresh
          </button>
          <button className="wr-button" type="button" disabled={busy} onClick={() => void sendTestRequest()}>
            <Send size={15} aria-hidden />
            Send test request
          </button>
        </div>
      </header>
      {notice ? <div className={`wr-notice wr-notice--${notice.tone}`}>{notice.text}</div> : null}
      <AdminState
        status={overview.status}
        error={overview.error}
        onRetry={() => void overview.reload()}
        emptyText="Install a store and seed data before loading the admin dashboard."
      >
        <section className="wr-stats-grid wr-overview-metrics">
          {data?.metrics.map((metric) => (
            <StatCard key={metric.label} {...metric} />
          ))}
        </section>

        <section className="wr-overview-layout">
          <div className="wr-panel wr-overview-funnel">
            <div className="wr-section-heading wr-section-heading--compact">
              <div>
                <h2>Review funnel</h2>
                <p>Where customers are moving from request to published proof.</p>
              </div>
              <Activity size={18} aria-hidden />
            </div>
            <div className="wr-funnel-bars">
              {data?.funnel.map((row) => (
                <div key={row.label} className="wr-funnel-row">
                  <span>{row.label}</span>
                  <div>
                    <i style={{ inlineSize: `${Math.max(4, Math.round((row.value / maxFunnelValue) * 100))}%` }} />
                  </div>
                  <strong>{row.value.toLocaleString()}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="wr-panel wr-overview-tasks">
            <div className="wr-section-heading wr-section-heading--compact">
              <div>
                <h2>Attention queue</h2>
                <p>{urgentItems ? `${urgentItems} items need review today.` : "No urgent blocks in the review system."}</p>
              </div>
              <Bell size={18} aria-hidden />
            </div>
            <ul className="wr-action-list wr-action-list--polished">
              {data?.actionQueue.map((item) => (
                <li key={item.label}>
                  <StatusBadge tone={item.tone}>{item.label}</StatusBadge>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="wr-panel wr-overview-scheduled">
            <div className="wr-section-heading wr-section-heading--compact">
              <div>
                <h2>Scheduled sends</h2>
                <p>
                  {data?.queueSummary.nextSendAt
                    ? `Next send ${formatDateTime(data.queueSummary.nextSendAt)} Cairo.`
                    : "No future review requests are queued."}
                </p>
              </div>
              <Clock3 size={18} aria-hidden />
            </div>
            <div className="wr-queue-summary">
              <div>
                <span>Future jobs</span>
                <strong>{(data?.queueSummary.queuedCount ?? 0).toLocaleString()}</strong>
              </div>
              <div>
                <span>Active requests</span>
                <strong>{(data?.queueSummary.activeRequestCount ?? 0).toLocaleString()}</strong>
              </div>
            </div>
            {data?.queueSummary.items.length ? (
              <ol className="wr-queue-list" aria-label="Upcoming scheduled review request sends">
                {data.queueSummary.items.map((item) => (
                  <li key={item.id}>
                    <strong>{item.order}</strong>
                    <span>{formatDateTime(item.dueAt)}</span>
                    <StatusBadge tone="info">{item.status}</StatusBadge>
                  </li>
                ))}
              </ol>
            ) : null}
          </div>

          <div className="wr-panel wr-overview-requests">
            <div className="wr-section-heading wr-section-heading--compact">
              <div>
                <h2>Request queue</h2>
                <p>Recent review requests waiting for customer action.</p>
              </div>
              <Link to="/admin/review-requests">View all</Link>
            </div>
            <div className="wr-request-table">
              <div>
                <span>Order</span>
                <span>Customer</span>
                <span>Products</span>
                <span>Due</span>
                <span>Status</span>
              </div>
              {data?.requests.map((row) => (
                <div key={row.id}>
                  <strong>{row.order}</strong>
                  <span>{row.customer}</span>
                  <span>{row.products}</span>
                  <span>{row.due}</span>
                  <StatusBadge tone={row.rawStatus === "FAILED" ? "critical" : row.rawStatus === "SUBMITTED" ? "success" : "info"}>
                    {row.status}
                  </StatusBadge>
                </div>
              ))}
            </div>
          </div>

          <div className="wr-panel wr-overview-side">
            <div className="wr-section-heading wr-section-heading--compact">
              <div>
                <h2>Review status mix</h2>
                <p>{totalStatus.toLocaleString()} total reviews by moderation state.</p>
              </div>
              <CheckCircle2 size={18} aria-hidden />
            </div>
            <div className="wr-status-mix">
              {data?.statusMix.map((row) => (
                <div key={row.label}>
                  <span>{row.label}</span>
                  <strong>{row.value.toLocaleString()}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="wr-command-grid wr-overview-shortcuts" aria-label="Primary review operations">
          <Link to="/admin/review-requests" className="wr-command-card">
            <Clock3 size={18} aria-hidden />
            <div>
              <strong>Schedule and send</strong>
              <span>Patch delivered orders, inspect timing, and queue WhatsApp/email sends.</span>
            </div>
          </Link>
          <Link to="/admin/reviews" className="wr-command-card">
            <ShieldCheck size={18} aria-hidden />
            <div>
              <strong>Moderate customer replies</strong>
              <span>Publish, reject, edit, reply, feature, and restart a review flow.</span>
            </div>
          </Link>
          <Link to="/admin/widgets" className="wr-command-card">
            <Store size={18} aria-hidden />
            <div>
              <strong>Storefront proof</strong>
              <span>Control ratings, galleries, carousels, snippets, and popups.</span>
            </div>
          </Link>
          <Link to="/admin/analytics" className="wr-command-card">
            <Eye size={18} aria-hidden />
            <div>
              <strong>Performance readout</strong>
              <span>Track delivery, submissions, media rate, and widget engagement.</span>
            </div>
          </Link>
        </section>
      </AdminState>
    </div>
  );
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    timeZone: "Africa/Cairo",
  }).format(new Date(value));
}
